import {Card, CardContent, CardActions, Button, Typography, CardProps, Chip} from "@mui/material";
import {FC, useContext, Dispatch, SetStateAction} from "react";
import {makeStyles} from "tss-react/mui";
import {NoteInput} from "./NoteInput";
import {IconContext} from "@buildwitt/shapeshift-identity";
import {formatTimestamp} from "@/app2/shared/utils/dateManipulation";
import ConfirmationDialog from "@/components/common/dialogs/ConfirmationDialog";
import {useSSTranslation} from "@/i18n/useSSTranslation";
import {NoteDto} from "@/app2/shared/data";
import CircularProgress from "@mui/material/CircularProgress";

const useStyles = makeStyles()((theme) => ({
	noteItemCard: {
		backgroundColor: theme.palette.background.paper,
		border: `1px solid ${theme.palette.secondary.light}`
	},
	cardContent: {
		display: "flex",
		flexDirection: "column",
		height: "auto",
		paddingBottom: theme.spacing(3)
	},
	cardActions: {
		borderTop: `1px solid ${theme.palette.secondary.main}20`,
		paddingTop: theme.spacing(1.5),
		paddingBottom: theme.spacing(1.5),
		"&>:not(:first-of-type)": {
			marginLeft: 0
		}
	},
	actionButton:{
		width:"50%",
		paddingTop: theme.spacing(0.5),
		paddingBottom: theme.spacing(0.5),
		"&:not(:first-of-type)": {
			borderLeft: `1px solid ${theme.palette.secondary.main}20`
		},
		"& svg": {
			fontSize: `${theme.iconSizes.extraLarge} !important`
		},
		borderRadius: 0
	},
	timestampButton: {
		backgroundColor: theme.palette.background.default,
		border: `1px solid ${theme.palette.secondary.light}`,
		padding: theme.spacing(0.5, 1.2, 0.5, 1.5),
		marginBottom: theme.spacing(1.5)
	},
	timestampIcon:{
		marginRight: 0,
		transform: "translateY(-0.5px)",
		color: theme.palette.secondary.main
	},
	timestampLabel: {
		marginRight: theme.spacing(1),
		color: theme.palette.secondary.main
	},
	wysiwygRoot: {
		whiteSpace: "pre-wrap",
		"&>p": {
			marginBottom: theme.spacing(0.5)
		}
	},
	linksRoot:{
		color: theme.palette.grey[300],
		marginTop: theme.spacing(1)
	},
	noteInput: {
	},
	savingRow: {
		display: "flex",
		alignItems: "center",
		gap: theme.spacing(1),
		marginTop: theme.spacing(0.5),
		minHeight: theme.spacing(3)
	},
	noteContentWithSaving: {
		display: "flex",
		flexDirection: "column",
		gap: theme.spacing(0.5)
	},
	savingSpinner: {
		width: 14,
		height: 14,
		color: theme.palette.primary.main
	},
	buttonIcon: {
	}
}));

interface ICustomProps {
	note: NoteDto,
	isSourceInvalid: boolean,
	onGoToModuleAction?: (courseSlug: string, moduleSlug:string, moduleType:string, timestamp?:number) => void
	isEditing: boolean,
	setIsEditing: Dispatch<SetStateAction<boolean>>,
	confirmDeleteModalOpen: boolean,
	setConfirmDeleteModalOpen: (open:boolean) => void,
	handleDeleteNote: () => void,
	handleUpdate: (newValue: string) => void,
	links: JSX.Element[],
	isUpdating?: boolean,
	isDeleting?: boolean,
}

export type IProps = ICustomProps & CardProps;

export const NoteItemCard:FC<IProps> = ({
	note, 
	className, 
	onGoToModuleAction,
	isSourceInvalid,
	isEditing, 
	confirmDeleteModalOpen, 
	handleDeleteNote, 
	handleUpdate, 
	links, 
	setIsEditing, 
	setConfirmDeleteModalOpen, 
	isUpdating = false,
	isDeleting = false,
	...baseProps
}) => {
	const {classes, cx} = useStyles();
	const {t} = useSSTranslation();
	const {course, module} = note;
	const {icon} = useContext(IconContext);

	const handleToggleEditing = (): void => {
		setIsEditing(state => !state);
	}

	return (
		<Card className={cx(classes.noteItemCard, className)} {...baseProps}>
			<CardContent className={classes.cardContent}>
				{note.videoTimestamp ?
					<Chip
						label={formatTimestamp(note.videoTimestamp)}
						className={classes.timestampButton}
						disabled={isSourceInvalid}
						onClick={() => {
							if(isSourceInvalid || !course || !module) {
								return
							}
							onGoToModuleAction?.(
								course?.slug ?? "", 
								module?.slug ?? "", 
								module?.type, 
								note.videoTimestamp ?? 0
							)
						}}
						icon={icon("clockIcon", {
							className: classes.timestampIcon
						})}
					/> : null}
				{isEditing
					? isUpdating
						? (
							<div className={classes.noteContentWithSaving} role="status" aria-live="polite">
								<Typography variant="caption" component="div" className={classes.wysiwygRoot}>
									{note.content.replace(/\n/gi, "\u00A0 \n")}
								</Typography>
								<span className={classes.savingRow}>
									<CircularProgress className={classes.savingSpinner} size={14} />
									<Typography variant="caption" color="text.secondary">
										{t("common_notes_saving", "Saving…")}
									</Typography>
								</span>
							</div>
						)
						: <NoteInput className={classes.noteInput} initialValue={note.content} onPostNote={handleUpdate} />
					: <Typography variant="caption" component="div" className={classes.wysiwygRoot}>
						{note.content.replace(/\n/gi, "\u00A0 \n")}
					</Typography>}
				{links.length ? <Typography variant="caption" className={classes.linksRoot}>{links}</Typography> : null}
			</CardContent>
			<CardActions className={classes.cardActions}>
				<Button 
					size="large" 
					variant="link" 
					color="primary" 
					className={classes.actionButton} 
					onClick={handleToggleEditing} 
					startIcon={icon("editIcon", {
						className: classes.buttonIcon
					})}
				>
					{isEditing ? t("common_notes_cancel", "Cancel") : t("common_notes_edit", "Edit")}
				</Button>

				<Button
					size="large" 
					variant="link" 
					color="primary" 
					className={classes.actionButton} 
					onClick={() => setConfirmDeleteModalOpen(true)} 
					startIcon={icon("deleteIcon", {className: classes.buttonIcon})}
				>
					{t("common_notes_delete", "Delete")}
				</Button>
				<ConfirmationDialog
					open={confirmDeleteModalOpen}
					title={t("notes_delete_confirm_title", "Are you sure?")}
					testIdPrefix="confirmDeleteNote"
					onCancel={() => setConfirmDeleteModalOpen(false)}
					onConfirm={handleDeleteNote}
					confirmButtonTitle={t("common_confirm", "Confirm")}
					disableConfirmButton={isDeleting}
					hideCloseIcon
				>
					<Typography variant="body1" sx={{color: "text.primary"}}>
					{t("notes_delete_confirm_content", "Once you delete this item, you won't be able to get it back.")}
					</Typography>
				</ConfirmationDialog>
			</CardActions>
		</Card>
	)
}